import { isExploring, phaseOfMove, type ReviewState } from '@chess/api-review';
import { type Phase } from '@chess/api-model';

// Which stage tab to open as the board moves through the game. A stage is
// opened only when the position on the board is a position of the actual game
// (not a line the reader is exploring, not while the board's Controls constraint holds it, as a puzzle does) and its
// stage differs from the last one opened for that game, so a reader who picked
// another tab by hand is not pulled back on every step within a stage.

/** What was last opened: the game and the stage. */
export interface Followed {
  gameId?: string;
  phase?: Phase;
}

/**
 * The stage of the position after `ply` moves: the stage of the move that
 * reached it; the start position is the opening. The review API's spans are
 * exclusive and end on the move where a stage is scored, so stepping onto a
 * stage's last move, or clicking its header's "to" link, keeps its tab, and
 * the next stage's tab opens on the move after it.
 */
export function phaseOfPly(state: ReviewState): Phase | undefined {
  const game = state.game;
  if (!game?.phases) return undefined;
  if (state.ply === 0) return 'opening';
  return phaseOfMove(game.phases, game.moves.length, state.ply, game.sides?.reader);
}

/** The stage to open now, or undefined to leave the tabs as they are. */
export function stageToOpen(state: ReviewState, followed: Followed, controlsHeld: boolean): Phase | undefined {
  const game = state.game;
  if (!game || isExploring(state) || controlsHeld) return undefined;
  const phase = phaseOfPly(state);
  if (!phase) return undefined;
  if (followed.gameId === game.id && followed.phase === phase) return undefined;
  return phase;
}
